import { useCallback, useState } from 'react'

import { Layout } from '@loadsmart/miranda-react'

import { Icon } from 'core/ui/components/icon/Icon'

import type { Shipment } from '../domain/Shipment'
import type { ShipmentCharge } from '../domain/ShipmentCharges'
import { useShipmentAccountingSummary } from './hooks/useShipmentAccountingSummary'
import { useShipmentCharges } from './hooks/useShipmentCharges'
import { NewChargeDialog } from './NewChargeDialog'
import { ShipmentAccountingSummary } from './ShipmentAccountingSummary'
import { ResponsiveButton, ResponsiveText } from './ShipmentCharges.styles'
import { ShipmentChargesTable } from './ShipmentChargesTable'

type ShipmentChargesProps = {
  shipment: Shipment
}

export function ShipmentCharges({ shipment }: ShipmentChargesProps) {
  const { summary, summaryStatus, refetchSummary } = useShipmentAccountingSummary(shipment.uuid)
  const {
    shipmentCharges,
    shipmentChargesStatus,
    sortDescriptor,
    onSort,
    fetchShipmentCharges,
    refetchShipmentCharges,
    addChargeToList,
    updateCharge,
  } = useShipmentCharges()

  const [createChargeDialogStatus, setCreateChargeDialogStatus] = useState({
    mounted: false,
    open: false,
  })

  const onChargeCreated = useCallback(
    (createdCharge: ShipmentCharge) => {
      addChargeToList(createdCharge)
      refetchSummary()
    },
    [addChargeToList, refetchSummary]
  )

  const onChargeUpdated = useCallback(
    (updatedCharge: ShipmentCharge) => {
      updateCharge(updatedCharge)
    },
    [updateCharge]
  )

  const onNewChargeDialogClose = useCallback(() => {
    setCreateChargeDialogStatus(prevStatus => ({ ...prevStatus, open: false }))
  }, [])

  const onNewChargeDialogClosed = useCallback(() => {
    setCreateChargeDialogStatus(prevStatus => ({ ...prevStatus, mounted: false }))
  }, [])

  function handleClickSubmitCharge() {
    setCreateChargeDialogStatus({ mounted: true, open: true })
  }

  return (
    <Layout.Stack gap="spacing-5">
      <Layout.Group justify="space-between">
        <ResponsiveText variant="body-lg-bold" color="color-text-tertiary">
          Send accessorial charges to shippers
        </ResponsiveText>
        <ResponsiveButton
          type="button"
          variant="primary"
          leading={<Icon name="plus" width="16" height="16" />}
          onClick={handleClickSubmitCharge}
        >
          Submit new charge
        </ResponsiveButton>
      </Layout.Group>
      <ShipmentAccountingSummary summary={summary} summaryStatus={summaryStatus} />
      <ShipmentChargesTable
        shipment={shipment}
        shipmentCharges={shipmentCharges}
        shipmentChargesStatus={shipmentChargesStatus}
        sortDescriptor={sortDescriptor}
        onSort={onSort}
        fetchShipmentCharges={fetchShipmentCharges}
        refetchShipmentCharges={refetchShipmentCharges}
        onUpdateCharge={updateCharge}
      />
      {createChargeDialogStatus.mounted && (
        <NewChargeDialog
          isOpen={createChargeDialogStatus.open}
          onClose={onNewChargeDialogClose}
          onClosed={onNewChargeDialogClosed}
          shipment={shipment}
          onChargeCreated={onChargeCreated}
          onChargeUpdated={onChargeUpdated}
        />
      )}
    </Layout.Stack>
  )
}
